import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Globe, DollarSign, MapPin, Bell, ChevronRight } from 'lucide-react'
import { t, getCurrentLanguage, setLanguage, SUPPORTED_LANGUAGES, type Language } from '../../i18n/translations'
import { CURRENCIES } from '../../utils/constants'
import LocationSelector from '../ui/LocationSelector'
import { productApi } from '../../services/api'
import { logger } from '../../utils/logger'
import PageLayout, { PageHeader } from '../layout/PageLayout'

interface UserPreferences {
  language: string
  default_currency: string
  home_country_code: string | null
  home_city_name: string | null
  notification_preferences: {
    interest: boolean
    favorite: boolean
    price_change: boolean
    reservation: boolean
    message: boolean
  }
}

export default function Settings() {
  const navigate = useNavigate()

  const [preferences, setPreferences] = useState<UserPreferences>({
    language: getCurrentLanguage(),
    default_currency: 'USD',
    home_country_code: null,
    home_city_name: null,
    notification_preferences: {
      interest: true,
      favorite: true,
      price_change: true,
      reservation: true,
      message: true,
    },
  })

  const [, setLoading] = useState(true)
  const [, setSaving] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showCurrencyModal, setShowCurrencyModal] = useState(false)

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        const location = await productApi.getUserLocation()
        setPreferences(prev => ({
          ...prev,
          home_country_code: location.home_country_code,
          home_city_name: location.home_city_name,
        }))
      } catch (error) {
        logger.warn('Failed to fetch user preferences', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUserPreferences()
  }, [])

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang)
    setPreferences(prev => ({ ...prev, language: lang }))
    setShowLanguageModal(false)
    // Reload to apply new language
    window.location.reload()
  }

  const handleCurrencyChange = (currency: string) => {
    setPreferences(prev => ({ ...prev, default_currency: currency }))
    setShowCurrencyModal(false)
    // TODO: Save to backend
  }

  const handleLocationChange = async (country: string | null, city: string | null) => {
    setPreferences(prev => ({
      ...prev,
      home_country_code: country,
      home_city_name: city,
    }))

    try {
      setSaving(true)
      await productApi.updateUserLocation({
        home_country_code: country,
        home_city_name: city,
      })
      logger.info('Location updated successfully')
    } catch (error) {
      logger.error('Failed to update location', error)
    } finally {
      setSaving(false)
    }
  }

  const handleNotificationToggle = (key: keyof UserPreferences['notification_preferences']) => {
    setPreferences(prev => ({
      ...prev,
      notification_preferences: {
        ...prev.notification_preferences,
        [key]: !prev.notification_preferences[key],
      },
    }))
    // TODO: Save to backend
  }

  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === preferences.language)
  const currentCurrency = CURRENCIES.find(c => c.code === preferences.default_currency)

  // Header component
  const header = (
    <PageHeader className="px-4 py-3">
      <div className="flex items-center justify-between max-w-md mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="font-medium">{t('common.back')}</span>
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          {t('common.settings')}
        </h1>
        <div className="w-16" />
      </div>
    </PageHeader>
  )

  return (
    <PageLayout header={header} stickyHeader>
      <div className="max-w-md mx-auto space-y-4">
        {/* Language */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <button
            onClick={() => setShowLanguageModal(true)}
            className="w-full p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-800">{t('common.language')}</p>
                <p className="text-sm text-gray-500">{currentLanguage?.nativeName}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Currency */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <button
            onClick={() => setShowCurrencyModal(true)}
            className="w-full p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-800">{t('common.currency')}</p>
                <p className="text-sm text-gray-500">
                  {currentCurrency?.code} ({currentCurrency?.symbol})
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="w-5 h-5 text-gray-500" />
            <p className="font-medium text-gray-800">{t('profile.defaultLocation')}</p>
          </div>
          <LocationSelector
            countryCode={preferences.home_country_code}
            cityName={preferences.home_city_name}
            onChange={handleLocationChange}
          />
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-5 h-5 text-gray-500" />
            <p className="font-medium text-gray-800">{t('notifications.settings')}</p>
          </div>

          <div className="space-y-3">
            {[
              { key: 'interest', title: t('notifications.interest'), desc: t('notifications.interestDesc') },
              { key: 'favorite', title: t('notifications.favorite'), desc: t('notifications.favoriteDesc') },
              { key: 'price_change', title: t('notifications.priceChange'), desc: t('notifications.priceChangeDesc') },
              { key: 'reservation', title: t('notifications.reservation'), desc: t('notifications.reservationDesc') },
              { key: 'message', title: t('notifications.messages'), desc: t('notifications.messagesDesc') },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-gray-800">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
                <button
                  onClick={() => handleNotificationToggle(item.key as keyof UserPreferences['notification_preferences'])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.notification_preferences[item.key as keyof UserPreferences['notification_preferences']]
                      ? 'bg-blue-500'
                      : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.notification_preferences[item.key as keyof UserPreferences['notification_preferences']]
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <button
            onClick={() => navigate('/page/privacy-policy')}
            className="w-full p-4 flex items-center justify-between border-b border-gray-100"
          >
            <span className="text-gray-800">{t('legal.privacyPolicy')}</span>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
          <button
            onClick={() => navigate('/page/terms-of-service')}
            className="w-full p-4 flex items-center justify-between border-b border-gray-100"
          >
            <span className="text-gray-800">{t('legal.termsOfService')}</span>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
          <button
            onClick={() => navigate('/page/about')}
            className="w-full p-4 flex items-center justify-between"
          >
            <span className="text-gray-800">{t('legal.about')}</span>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Language Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-96 rounded-t-2xl sm:rounded-2xl p-4 animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('common.language')}</h3>
            <div className="space-y-2">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code as Language)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    preferences.language === lang.code
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'hover:bg-gray-50 text-gray-800'
                  }`}
                >
                  {lang.nativeName} ({lang.name})
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowLanguageModal(false)}
              className="w-full mt-4 p-3 text-gray-500 hover:text-gray-700"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Currency Modal */}
      {showCurrencyModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-96 rounded-t-2xl sm:rounded-2xl p-4 max-h-[70vh] overflow-auto animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('common.currency')}</h3>
            <div className="space-y-2">
              {CURRENCIES.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => handleCurrencyChange(currency.code)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    preferences.default_currency === currency.code
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'hover:bg-gray-50 text-gray-800'
                  }`}
                >
                  {currency.symbol} {currency.code} - {currency.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowCurrencyModal(false)}
              className="w-full mt-4 p-3 text-gray-500 hover:text-gray-700"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
